import { useState } from "react";
import { ArrowUpRight, Mail, MapPin, CalendarClock } from "lucide-react";
import { openCalendly } from "../lib/calendly";

const CONTACT_EMAIL = "hello@StoutRoad.com";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

    const handleSubmit = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(
            `Stout Road inquiry — ${form.name || "New contact"}`
        );
        const body = encodeURIComponent(
            `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\n\n${form.message}`
        );
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    };

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-28 md:py-40 px-6 md:px-12 lg:px-20 bg-[#07050C] border-t border-[rgba(163,168,216,0.1)] overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute -bottom-40 -left-40 w-[640px] h-[640px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(91,58,120,0.25) 0%, transparent 70%)",
                    filter: "blur(60px)",
                }}
            />

            <div className="relative max-w-[1400px] mx-auto grid grid-cols-12 gap-10 md:gap-12">
                <div className="col-span-12 lg:col-span-6">
                    <p className="overline flex items-center gap-3">
                        <span className="lime-dot" />
                        Get in touch
                    </p>
                    <h2
                        data-testid="contact-headline"
                        className="font-heading text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6"
                    >
                        Let's build your{" "}
                        <span className="italic text-[#A3A8D8]">
                            financial foundation
                        </span>
                        .
                    </h2>
                    <p className="mt-6 max-w-lg text-white/60 text-base md:text-lg leading-relaxed font-light">
                        Tell us a little about your company and where you need traction.
                        We'll be in touch within one business day.
                    </p>

                    <div className="mt-12 space-y-6">
                        <button
                            data-testid="contact-calendly-link"
                            onClick={openCalendly}
                            className="flex items-start gap-4 group text-left w-full"
                        >
                            <div className="w-10 h-10 rounded-sm flex items-center justify-center border border-[rgba(198,244,50,0.45)] text-[#C6F432] bg-[rgba(198,244,50,0.08)] group-hover:bg-[rgba(198,244,50,0.16)] transition-colors duration-300">
                                <CalendarClock size={16} strokeWidth={1.5} />
                            </div>
                            <div>
                                <p className="overline text-white/40">
                                    Book directly
                                </p>
                                <p className="font-heading text-2xl text-white sr-link mt-1 inline-flex items-center gap-2">
                                    Schedule a 30-min call
                                    <ArrowUpRight
                                        size={18}
                                        className="text-[#C6F432]"
                                    />
                                </p>
                            </div>
                        </button>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="contact-email-link"
                            className="flex items-start gap-4 group"
                        >
                            <div className="w-10 h-10 rounded-sm flex items-center justify-center border border-[rgba(163,168,216,0.2)] text-[#A3A8D8] group-hover:border-[#C6F432] group-hover:text-[#C6F432] transition-colors duration-300">
                                <Mail size={16} strokeWidth={1.5} />
                            </div>
                            <div>
                                <p className="overline text-white/40">Email</p>
                                <p className="font-heading text-2xl text-white sr-link mt-1">
                                    {CONTACT_EMAIL}
                                </p>
                            </div>
                        </a>
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-sm flex items-center justify-center border border-[rgba(163,168,216,0.2)] text-[#A3A8D8]">
                                <MapPin size={16} strokeWidth={1.5} />
                            </div>
                            <div>
                                <p className="overline text-white/40">Visit</p>
                                <p className="font-heading text-2xl text-white mt-1">
                                    StoutRoad.com
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-6 lg:pl-12">
                    <form
                        data-testid="contact-form"
                        onSubmit={handleSubmit}
                        className="space-y-8 max-w-lg lg:ml-auto"
                    >
                        <div>
                            <label className="overline text-white/40 block mb-2">
                                Name
                            </label>
                            <input
                                data-testid="contact-input-name"
                                type="text"
                                className="sr-input"
                                placeholder="Jane Doe"
                                value={form.name}
                                onChange={update("name")}
                                required
                            />
                        </div>
                        <div>
                            <label className="overline text-white/40 block mb-2">
                                Email
                            </label>
                            <input
                                data-testid="contact-input-email"
                                type="email"
                                className="sr-input"
                                placeholder="jane@company.com"
                                value={form.email}
                                onChange={update("email")}
                                required
                            />
                        </div>
                        <div>
                            <label className="overline text-white/40 block mb-2">
                                Company
                            </label>
                            <input
                                data-testid="contact-input-company"
                                type="text"
                                className="sr-input"
                                placeholder="Company name"
                                value={form.company}
                                onChange={update("company")}
                            />
                        </div>
                        <div>
                            <label className="overline text-white/40 block mb-2">
                                How can we help?
                            </label>
                            <textarea
                                data-testid="contact-input-message"
                                className="sr-input sr-textarea"
                                placeholder="A few sentences about your situation…"
                                value={form.message}
                                onChange={update("message")}
                                required
                            />
                        </div>

                        <button
                            data-testid="contact-submit-button"
                            type="submit"
                            className="btn-primary w-full justify-center"
                        >
                            Send Inquiry
                            <ArrowUpRight size={14} />
                        </button>
                        <p className="text-xs text-white/35 leading-relaxed">
                            Submitting opens your email client addressed to{" "}
                            <span className="text-[#A3A8D8]">{CONTACT_EMAIL}</span>.
                        </p>
                    </form>
                </div>
            </div>
        </section>
    );
}
